import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { filterGroupFor } from '@/components/workflows/sidebarGrouping';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2, Plus, Play, Pause, Trash2, Workflow, Clock, Zap,
  ShipWheel, Edit, Sparkles, ChevronDown, ChevronUp, ShieldCheck, Puzzle,
} from 'lucide-react';
import {
  LineChart, Line, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { apiClient, type WorkflowSummary, type WorkflowStats, type SuggestedWorkflow, type CustomBlock } from '../api/client';
import { WorkflowFilters, type WorkflowFilterValues } from '@/components/WorkflowFilters';

import slackColorIcon from '../assets/icons/slack-color.png';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import confluenceIcon from '../assets/icons/confluence.svg';
import jiraIcon from '../assets/icons/jira.svg';
import linearIcon from '../assets/icons/linear.png';
import linearDarkIcon from '../assets/icons/linear-dark-mode.png';
import datadogLightIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';
import argocdIcon from '../assets/icons/argocd.svg';
import fluxcdIcon from '../assets/icons/flux-cd.svg';
import karpenterIcon from '../assets/icons/karpenter.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import helmLightIcon from '../assets/icons/helm-light.svg';
import helmDarkIcon from '../assets/icons/helm-dark.svg';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import { ReactComponent as GCPListIcon } from '../assets/icons/gcp.svg';
import cloudflareIcon from '../assets/icons/cloudflare.svg';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';

const STATUS_BADGES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  draft:    { variant: 'secondary', label: 'Draft' },
  active:   { variant: 'default', label: 'Active' },
  paused:   { variant: 'outline', label: 'Paused' },
  archived: { variant: 'destructive', label: 'Archived' },
};

function getWorkflowIntegrations(wf: WorkflowSummary): string[] {
  const integrations = new Set<string>();
  const hasKestrel = new Set<string>();
  const nodes = wf.definition?.nodes || [];
  for (const node of nodes) {
    const d = node.data || {};
    // Every family of a cloud provider collapses to one provider key, so
    // filtering by "AWS" or "Google Cloud" finds a workflow built from any of
    // them — including the cost blocks, which are tagged aws-cost / gcp-cost.
    if (d.integration && d.integration !== 'kestrel' && d.integration !== 'custom' && d.integration !== 'approval') integrations.add(filterGroupFor(d.integration));
    if (d.integration === 'custom') {
      const iName = d.integration_name || d.action || 'custom';
      integrations.add(`custom:${iName}`);
    }
    if (d.integration === 'kestrel') hasKestrel.add('kestrel');
    if (d.source === 'aws') integrations.add('aws');
    if (d.source === 'kubernetes') integrations.add('kubernetes');
    if (d.source === 'pagerduty') integrations.add('pagerduty');
    if (d.source === 'posthog') integrations.add('posthog');
    if (d.source === 'vercel') integrations.add('vercel');
    if (d.source === 'railway') integrations.add('railway');
    if (d.source === 'flyio') integrations.add('flyio');
    if (d.source === 'nebius') integrations.add('nebius');
    if (d.source === 'jenkins') integrations.add('jenkins');
    if (d.source === 'circleci') integrations.add('circleci');
    if (d.source === 'sonarcloud') integrations.add('sonarcloud');
    if (d.source === 'okta') integrations.add('okta');
    if (d.source === 'databricks') integrations.add('databricks');
    if (d.source === 'gcp') integrations.add('gcp');
    if (d.source === 'kyverno') integrations.add('kyverno');
    if (d.source === 'trivy') integrations.add('trivy');
    if (d.source === 'terraform') integrations.add('terraform');
    if (d.source === 'pulumi') integrations.add('pulumi');
    if (d.source === 'github') integrations.add('github');
    if (d.source === 'request') {
      const reqCats = d.signal_filter?.request_categories || [];
      if (reqCats.includes('k8s')) integrations.add('kubernetes');
      else if (reqCats.includes('cloud')) integrations.add('aws');
      else if (reqCats.includes('gcp')) integrations.add('gcp');
      else if (reqCats.includes('databricks')) integrations.add('databricks');
      else if (reqCats.includes('cloudflare')) integrations.add('cloudflare');
      else if (reqCats.includes('pagerduty')) integrations.add('pagerduty');
      else if (reqCats.includes('datadog')) integrations.add('datadog');
      else if (reqCats.includes('argocd')) integrations.add('argocd');
      else if (reqCats.includes('argo-rollouts')) integrations.add('argo-rollouts');
      else if (reqCats.includes('fluxcd')) integrations.add('fluxcd');
      else if (reqCats.includes('karpenter')) integrations.add('karpenter');
      else if (reqCats.includes('kyverno')) integrations.add('kyverno');
      else if (reqCats.includes('trivy')) integrations.add('trivy');
      else if (reqCats.includes('github')) integrations.add('github');
      else if (reqCats.includes('gitlab')) integrations.add('gitlab');
      else if (reqCats.includes('helm')) integrations.add('helm');
      else if (reqCats.includes('vercel')) integrations.add('vercel');
      else if (reqCats.includes('terraform')) integrations.add('terraform');
      else if (reqCats.includes('pulumi')) integrations.add('pulumi');
      else integrations.add('slack');
    }
    if (d.source === 'custom_webhook') {
      const iName = d.integration_name || 'custom';
      integrations.add(`custom:${iName}`);
    }
  }
  const tcSource = wf.trigger_config?.source;
  if (tcSource === 'kubernetes') integrations.add('kubernetes');
  if (tcSource === 'aws') integrations.add('aws');
  if (tcSource === 'pagerduty') integrations.add('pagerduty');
  if (tcSource === 'posthog') integrations.add('posthog');
  if (tcSource === 'vercel') integrations.add('vercel');
  if (tcSource === 'railway') integrations.add('railway');
  if (tcSource === 'flyio') integrations.add('flyio');
  if (tcSource === 'nebius') integrations.add('nebius');
  if (tcSource === 'jenkins') integrations.add('jenkins');
  if (tcSource === 'circleci') integrations.add('circleci');
  if (tcSource === 'sonarcloud') integrations.add('sonarcloud');
  if (tcSource === 'okta') integrations.add('okta');
  if (tcSource === 'databricks') integrations.add('databricks');
  if (tcSource === 'gcp') integrations.add('gcp');
  if (tcSource === 'terraform') integrations.add('terraform');
  if (tcSource === 'pulumi') integrations.add('pulumi');
  if (tcSource === 'github') integrations.add('github');
  if (tcSource === 'karpenter') integrations.add('karpenter');
  if (tcSource === 'kyverno') integrations.add('kyverno');
  if (tcSource === 'trivy') integrations.add('trivy');
  if (tcSource === 'request') {
    const signals = wf.trigger_config?.signals || [];
    const reqCats = signals[0]?.filters?.request_categories || [];
    if (reqCats.includes('k8s')) integrations.add('kubernetes');
    else if (reqCats.includes('cloud')) integrations.add('aws');
    else if (reqCats.includes('gcp')) integrations.add('gcp');
    else if (reqCats.includes('databricks')) integrations.add('databricks');
    else if (reqCats.includes('cloudflare')) integrations.add('cloudflare');
    else if (reqCats.includes('pagerduty')) integrations.add('pagerduty');
    else if (reqCats.includes('datadog')) integrations.add('datadog');
    else if (reqCats.includes('argocd')) integrations.add('argocd');
    else if (reqCats.includes('argo-rollouts')) integrations.add('argo-rollouts');
    else if (reqCats.includes('fluxcd')) integrations.add('fluxcd');
    else if (reqCats.includes('karpenter')) integrations.add('karpenter');
    else if (reqCats.includes('kyverno')) integrations.add('kyverno');
    else if (reqCats.includes('trivy')) integrations.add('trivy');
    else if (reqCats.includes('github')) integrations.add('github');
    else if (reqCats.includes('gitlab')) integrations.add('gitlab');
    else if (reqCats.includes('helm')) integrations.add('helm');
    else if (reqCats.includes('vercel')) integrations.add('vercel');
    else if (reqCats.includes('terraform')) integrations.add('terraform');
    else if (reqCats.includes('pulumi')) integrations.add('pulumi');
    else integrations.add('slack');
  }
  // custom_webhook is handled by the node scan above — no tcSource fallback needed
  if (hasKestrel.size > 0) integrations.add('kestrel');
  if (integrations.size === 0) integrations.add('kestrel');
  return Array.from(integrations).slice(0, 4);
}

function getCustomIconMap(wf: WorkflowSummary, customBlocks?: CustomBlock[]): Record<string, { light: string; dark: string }> {
  const iconMap: Record<string, { light: string; dark: string }> = {};
  const nodes = wf.definition?.nodes || [];
  for (const node of nodes) {
    const d = node.data || {};
    if (d.integration === 'custom' || d.source === 'custom_webhook') {
      const iName = d.integration_name || d.action || 'custom';
      const key = `custom:${iName}`;
      if (iconMap[key]) continue;

      let light = d.custom_icon_url || '';
      let dark = d.custom_icon_url_dark || light;

      // Resolve from customBlocks by action ID (action blocks)
      if ((!light && !dark) && customBlocks && d.action) {
        const blockId = (d.action as string).replace('custom-', '');
        const cb = customBlocks.find(b => b.id === blockId);
        if (cb) {
          light = cb.icon_url || '';
          dark = cb.icon_url_dark || light;
          if (!light && !dark && cb.integration_name) {
            const groupBlock = customBlocks.find(b => b.integration_name === cb.integration_name && (b.icon_url || b.icon_url_dark));
            if (groupBlock) {
              light = groupBlock.icon_url || '';
              dark = groupBlock.icon_url_dark || light;
            }
          }
        }
      }
      // Resolve from customBlocks by custom_block_id
      if (!light && !dark && customBlocks && d.custom_block_id) {
        const cb = customBlocks.find(b => b.id === d.custom_block_id);
        if (cb) {
          light = cb.icon_url || '';
          dark = cb.icon_url_dark || light;
        }
      }
      // Resolve from customBlocks by template_id (trigger blocks from NL agent)
      if (!light && !dark && customBlocks && d.template_id) {
        const tid = (d.template_id as string).replace('custom-trigger-', '');
        const cb = customBlocks.find(b => b.id === tid);
        if (cb) {
          light = cb.icon_url || '';
          dark = cb.icon_url_dark || light;
        }
      }
      // Last resort: any trigger block in the same integration
      if (!light && !dark && customBlocks) {
        const cb = customBlocks.find(b => b.block_type === 'trigger' && (b.icon_url || b.icon_url_dark));
        if (cb) {
          light = cb.icon_url || '';
          dark = cb.icon_url_dark || light;
        }
      }
      iconMap[key] = { light: light || dark, dark: dark || light };
    }
  }
  return iconMap;
}

function IntegrationIconImg({ integration, isDark, size = 16, iconUrl }: { integration: string; isDark: boolean; size?: number; iconUrl?: string }) {
  const cls = `rounded`;
  const s = { width: size, height: size };
  if (integration === 'custom' && iconUrl) {
    return <img src={iconUrl} alt="Custom" style={s} className={`${cls} object-contain`} />;
  }
  if (integration.startsWith('custom:') && iconUrl) {
    return <img src={iconUrl} alt="Custom" style={s} className={`${cls} object-contain`} />;
  }
  switch (integration) {
    case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" style={s} className={cls} />;
    case 'gitlab': return <img src={gitlabIcon} alt="GitLab" style={s} className={cls} />;
    case 'slack': return <img src={slackColorIcon} alt="Slack" style={s} className={cls} />;
    case 'pagerduty': return <img src={pagerdutyColorIcon} alt="PagerDuty" style={s} className={cls} />;
    case 'confluence': return <img src={confluenceIcon} alt="Confluence" style={s} className={cls} />;
    case 'jira': return <img src={jiraIcon} alt="Jira" style={s} className={cls} />;
    case 'linear': return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" style={s} className={cls} />;
    case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" style={s} className={cls} />;
    case 'aws': case 'aws-cost': case 'aws-ops': case 'aws-security': case 'aws-data': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" style={s} className={cls} />;
    case 'argocd': return <img src={argocdIcon} alt="ArgoCD" style={s} className={cls} />;
    case 'argo-rollouts': return <img src={argocdIcon} alt="Argo Rollouts" style={s} className={cls} />;
    case 'fluxcd': return <img src={fluxcdIcon} alt="Flux CD" style={s} className={cls} />;
    case 'karpenter': return <img src={karpenterIcon} alt="Karpenter" style={s} className={cls} />;
    case 'kyverno': return <img src={kyvernoIcon} alt="Kyverno" style={s} className={cls} />;
    case 'trivy': return <img src={trivyIcon} alt="Trivy" style={s} className={cls} />;
    case 'helm': return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" style={s} className={cls} />;
    case 'posthog': return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" style={s} className={`${cls} object-contain`} />;
    case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'railway': return <img src={railwayIcon} alt="Railway" style={{ width: size * 0.8, height: size * 0.8, filter: isDark ? 'invert(1)' : 'none' }} className={`${cls} object-contain`} />;
    case 'flyio': return <img src={flyioIcon} alt="Fly.io" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'nebius': return <img src={nebiusIcon} alt="Nebius" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'beam': return <BeamIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'daytona': return <DaytonaIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'supabase': return <img src={supabaseIcon} alt="Supabase" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'planetscale': return <PlanetScaleIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'neon': return <img src={neonIcon} alt="Neon" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'circleci': return <CircleCIIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'sonarcloud': return <SonarCloudIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'okta': return <OktaIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} text-foreground`} />;
    case 'databricks': return <img src={databricksIcon} alt="Databricks" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'gcp':
    case 'gcp-security':
    case 'gcp-data':
    case 'gcp-cost': return <GCPListIcon style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'terraform': return <img src={terraformIcon} alt="Terraform" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'vault': return <img src={vaultIcon} alt="Vault" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'infisical': return <img src={infisicalIcon} alt="Infisical" style={{ width: size * 0.8, height: size * 0.8 }} className={`${cls} object-contain`} />;
    case 'cloudflare': return <img src={cloudflareIcon} alt="Cloudflare" style={s} className={cls} />;
    case 'kestrel': return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" style={s} className={cls} />;
    case 'kubernetes': return <span style={s} className="flex items-center justify-center text-blue-500"><ShipWheel style={{ width: size, height: size }} /></span>;
    case 'approval': return <ShieldCheck style={{ width: size * 0.85, height: size * 0.85 }} className="text-amber-500" />;
    case 'custom': return <Puzzle style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    default:
      if (integration.startsWith('custom:')) return <Puzzle style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
      return <Workflow style={{ width: size * 0.85, height: size * 0.85 }} className="text-muted-foreground" />;
  }
}

function WorkflowIntegrationStack({ wf, customBlocks }: { wf: WorkflowSummary; customBlocks?: CustomBlock[] }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const integrations = getWorkflowIntegrations(wf);
  const customIconMap = getCustomIconMap(wf, customBlocks);

  const resolveIconUrl = (integ: string) => {
    if (integ.startsWith('custom:') || integ === 'custom') {
      const icons = customIconMap[integ];
      if (icons) return isDark ? icons.dark : icons.light;
    }
    return undefined;
  };

  if (integrations.length === 1) {
    return (
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/50">
        <IntegrationIconImg integration={integrations[0]} isDark={isDark} size={20} iconUrl={resolveIconUrl(integrations[0])} />
      </div>
    );
  }

  // Position layouts: 2 = side by side offset, 3+ = two top, rest bottom
  const positions: { top: number; left: number }[] = [];
  if (integrations.length === 2) {
    positions.push({ top: 0, left: 0 }, { top: 14, left: 19 });
  } else {
    // Row 1: first two icons
    positions.push({ top: 0, left: 0 }, { top: 0, left: 19 });
    // Row 2: remaining icons. For 3 icons: center single icon below. For 4: align to row 1.
    const row2Count = integrations.length - 2;
    const row2StartLeft = row2Count === 1 ? 10 : 0;
    for (let i = 2; i < integrations.length; i++) {
      positions.push({ top: 19, left: row2StartLeft + (i - 2) * 19 });
    }
  }

  return (
    <div className="relative w-12 h-10 shrink-0">
      {integrations.map((integ, i) => (
        <div
          key={integ}
          className="absolute bg-background border rounded-md p-0.5 shadow-sm flex items-center justify-center"
          style={{
            width: 22, height: 22,
            top: positions[i]?.top ?? 0,
            left: positions[i]?.left ?? 0,
            zIndex: integrations.length - i,
          }}
        >
          <IntegrationIconImg integration={integ} isDark={isDark} size={16} iconUrl={resolveIconUrl(integ)} />
        </div>
      ))}
    </div>
  );
}

const WorkflowList: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [totalWorkflows, setTotalWorkflows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [stats, setStats] = useState<WorkflowStats | null>(null);
  const [customBlocks, setCustomBlocks] = useState<CustomBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestedWorkflow[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestJustCompleted, setSuggestJustCompleted] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [filters, setFilters] = useState<WorkflowFilterValues>({
    name: '',
    integrations: [],
    createdBy: [],
    statuses: [],
    lastUpdated: '',
  });

  const [tenantUsers, setTenantUsers] = useState<Array<{ id: string; email: string; picture_url?: string }>>([]);

  useEffect(() => {
    if (!user) return;
    apiClient.listTenantUsers().then((data: any) => {
      const users = Array.isArray(data) ? data : (data?.users || []);
      setTenantUsers(users.map((u: any) => ({ id: u.user_id || u.id || u.email, email: u.email, picture_url: u.picture_url })));
    }).catch(() => {});
  }, [user]);

  const createdByOptions = useMemo(() => {
    const creatorEmails = new Set<string>();
    for (const wf of workflows) {
      if (wf.created_by) creatorEmails.add(wf.created_by);
    }
    return Array.from(creatorEmails).map(email => {
      const user = tenantUsers.find(u => u.email === email);
      return { id: email, email, pictureUrl: user?.picture_url };
    });
  }, [workflows, tenantUsers]);

  // Server-side filtering — workflows are already filtered by the API.
  // Keep a thin alias for rendering.
  const filteredWorkflows = workflows;

  // Convert frontend filter state to API query params.
  function buildApiFilters(f: WorkflowFilterValues): { status?: string; name?: string; created_by?: string; updated_since?: string; updated_until?: string; integrations?: string } {
    const result: { status?: string; name?: string; created_by?: string; updated_since?: string; updated_until?: string; integrations?: string } = {};
    if (f.statuses.length === 1) result.status = f.statuses[0];
    if (f.statuses.length > 1) result.status = f.statuses.join(',');
    if (f.name) result.name = f.name;
    if (f.createdBy.length > 0) result.created_by = f.createdBy.join(',');
    if (f.lastUpdated) {
      if (f.lastUpdated.includes(',')) {
        const [from, to] = f.lastUpdated.split(',');
        if (from) result.updated_since = from;
        if (to) result.updated_until = to;
      } else {
        result.updated_since = f.lastUpdated;
      }
    }
    if (f.integrations.length > 0) result.integrations = f.integrations.join(',');
    return result;
  }

  useEffect(() => {
    if (!authLoading && user) loadWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, currentPage, filters]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      try {
        const apiFilters = buildApiFilters(filters);
        const [data, s] = await Promise.all([
          apiClient.listWorkflowsPaginated(currentPage, pageSize, apiFilters),
          apiClient.getWorkflowStats(),
        ]);
        setWorkflows(data.workflows);
        setTotalWorkflows(data.total);
        setStats(s);
      } catch { /* silent refresh */ }
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentPage, filters]);

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const apiFilters = buildApiFilters(filters);
      const [data, s, sugg, cbs] = await Promise.all([
        apiClient.listWorkflowsPaginated(currentPage, pageSize, apiFilters),
        apiClient.getWorkflowStats().catch(() => null),
        apiClient.getSuggestedWorkflows().catch(() => []),
        apiClient.listCustomBlocks().catch(() => []),
      ]);
      setWorkflows(data.workflows);
      setTotalWorkflows(data.total);
      if (s) setStats(s);
      setCustomBlocks(cbs || []);
      // Check if suggestions are still being generated (marker row)
      const realSugg = (sugg || []).filter((sw: SuggestedWorkflow) => sw.name !== '__generating__');
      const isGenerating = (sugg || []).some((sw: SuggestedWorkflow) => sw.name === '__generating__');
      setSuggestions(realSugg);
      if (isGenerating && !suggestLoading) {
        setSuggestLoading(true);
        startSuggestionPolling();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const startSuggestionPolling = () => {
    let attempts = 0;
    const maxAttempts = 90;
    let hasExpandedOnce = false;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const sugg = await apiClient.getSuggestedWorkflows();
        const real = (sugg || []).filter((sw: SuggestedWorkflow) => sw.name !== '__generating__');
        const stillGenerating = (sugg || []).some((sw: SuggestedWorkflow) => sw.name === '__generating__');
        if (real.length > 0) {
          setSuggestions(real);
          if (!hasExpandedOnce) {
            setSuggestJustCompleted(true);
            hasExpandedOnce = true;
          }
        }
        if (!stillGenerating) {
          setSuggestLoading(false);
          if (real.length === 0) {
            setSuggestError('Generation failed. Please try again.');
          }
          clearInterval(poll);
        } else if (attempts >= maxAttempts) {
          setSuggestLoading(false);
          setSuggestError('Suggestions took too long. Try again.');
          clearInterval(poll);
        }
      } catch {
        // Keep polling
      }
    }, 2000);
  };

  const handleActivate = async (id: string) => {
    try {
      await apiClient.activateWorkflow(id);
      setWorkflows(prev => prev.map(w => w.id === id ? { ...w, status: 'active' } : w));
    } catch (err: any) {
      setError(err?.message || 'Failed to activate');
    }
  };

  const handlePause = async (id: string) => {
    try {
      await apiClient.pauseWorkflow(id);
      setWorkflows(prev => prev.map(w => w.id === id ? { ...w, status: 'paused' } : w));
    } catch (err: any) {
      setError(err?.message || 'Failed to pause');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.deleteWorkflow(deleteTarget);
      loadWorkflows();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleSuggest = () => {
    navigate('/workflows/suggestions', { state: { triggerGeneration: true } });
  };

  const handleUseSuggestion = (suggestion: SuggestedWorkflow) => {
    navigate('/workflows/new', {
      state: {
        suggestion: {
          name: suggestion.name,
          description: suggestion.description,
          definition: suggestion.definition,
          trigger_config: suggestion.trigger_config,
          nl_prompt: suggestion.nl_prompt || '',
        },
      },
    });
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-end gap-3">
        <button
          onClick={handleSuggest}
          className="suggest-btn relative inline-flex items-center gap-2 px-4 py-2 h-10 text-sm font-medium rounded-md text-foreground"
        >
          <span className="suggest-btn-border" />
          <span className="suggest-btn-bg" />
          <span className="relative z-10 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Suggest Workflows
          </span>
        </button>
        <Button onClick={() => navigate('/workflows/new')} className="gap-2">
          <Plus className="w-4 h-4" /> New Workflow
        </Button>
      </div>

      {suggestError && (
        <Alert variant="destructive">
          <AlertDescription>{suggestError}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Analytics charts — only show when there are executions */}
      {stats && stats.total_executions > 0 && stats.daily_executions && stats.daily_executions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MiniLineChart
            title="Total Executions"
            data={stats.daily_executions}
            dataKey="total"
            color="#3b82f6"
            computeTotal={(d) => d.completed + d.failed + d.running + d.waiting}
            stat={stats.total_executions}
          />
          <MiniLineChart
            title="Completed vs Failed"
            data={stats.daily_executions}
            lines={[
              { dataKey: 'completed', color: '#22c55e', name: 'Completed' },
              { dataKey: 'failed', color: '#ef4444', name: 'Failed' },
            ]}
            stat={stats.status_breakdown.completed || 0}
            statLabel="completed"
          />
          <MiniLineChart
            title="Blocked on Approval"
            data={stats.daily_executions}
            dataKey="waiting"
            color="#f59e0b"
            stat={stats.status_breakdown.waiting_approval || 0}
            statLabel="pending"
          />
        </div>
      )}

      {/* Filter panel — always show if workflows exist OR if filters are active */}
      {(workflows.length > 0 || filters.name || filters.integrations.length > 0 || filters.createdBy.length > 0 || filters.statuses.length > 0 || filters.lastUpdated) && (
        <WorkflowFilters
          filters={filters}
          onChange={v => { setFilters(v); setCurrentPage(1); }}
          createdByOptions={createdByOptions}
        />
      )}

      {/* Empty state — differentiate "no workflows exist" from "filters matched nothing" */}
      {workflows.length === 0 && !loading && (() => {
        const hasActiveFilters = filters.name || filters.integrations.length > 0 || filters.createdBy.length > 0 || filters.statuses.length > 0 || filters.lastUpdated;
        if (hasActiveFilters) {
          return (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No workflows match the current filters.
            </div>
          );
        }
        return (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <h3 className="text-lg font-semibold">No workflows yet</h3>
              <p className="text-muted-foreground mt-1 max-w-lg">
                Create your first workflow to automate incident response. Define triggers based on Kubernetes signals and chain together actions across your integrations.
              </p>
              <Button onClick={() => navigate('/workflows/new')} className="mt-4 gap-2">
                <Plus className="w-4 h-4" /> Create Workflow
              </Button>
            </CardContent>
          </Card>
        );
      })()}

      {/* No results from filters (legacy — kept for when client-side subset is empty) */}
      {workflows.length > 0 && filteredWorkflows.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No workflows match the current filters.
        </div>
      )}

      {/* Workflow cards */}
      <div className="grid gap-4">
        {filteredWorkflows.map(wf => {
          const badge = STATUS_BADGES[wf.status] || STATUS_BADGES.draft;
          const nodeCount = wf.definition?.nodes?.length || 0;
          return (
            <Card
              key={wf.id}
              className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
              onClick={() => navigate(`/workflows/${wf.id}`)}
            >
              <CardContent className="p-4">
                {/* Mobile: stack content above the action buttons so the
                    name/description get the full card width instead of
                    competing with the button column. sm:+ is the original
                    single-row layout, untouched. */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                    <WorkflowIntegrationStack wf={wf} customBlocks={customBlocks} />
                    <div className="min-w-0 flex-1 overflow-hidden">
                      {/* Mobile: pin the status badge(s) to the right edge of
                          the top row so they line up across cards instead of
                          trailing names of different lengths. Desktop keeps
                          the badge adjacent to the name (sm:justify-start). */}
                      <div className="flex items-center gap-2 min-w-0 justify-between sm:justify-start">
                        <h3 className="font-semibold truncate">{wf.name}</h3>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                          {wf.simulated && (
                            <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400">
                              Simulated
                            </Badge>
                          )}
                        </div>
                      </div>
                      {wf.description && (
                        <p className="text-sm text-muted-foreground truncate mt-0.5">{wf.description}</p>
                      )}
                      <div className="flex items-center gap-x-4 gap-y-1 sm:gap-y-4 text-xs text-muted-foreground mt-1 flex-wrap overflow-hidden">
                        <span className="flex items-center gap-1">
                          <Zap className="w-3 h-3" /> {nodeCount} steps
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {wf.trigger_count} triggers
                        </span>
                        {wf.last_triggered_at && (
                          <span>Last triggered: {new Date(wf.last_triggered_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC</span>
                        )}
                        <span>Last updated: {new Date(wf.updated_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC{wf.updated_by ? ` by ${wf.updated_by}` : ''}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 self-end sm:self-auto" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/workflows/${wf.id}/edit`)} title="Edit">
                      <Edit className="w-4 h-4" />
                    </Button>
                    {(wf.status === 'draft' || wf.status === 'paused') && !wf.simulated ? (
                      <Button variant="ghost" size="sm" onClick={() => handleActivate(wf.id)} title="Activate">
                        <Play className="w-4 h-4" />
                      </Button>
                    ) : wf.status === 'active' ? (
                      <Button variant="ghost" size="sm" onClick={() => handlePause(wf.id)} title="Pause">
                        <Pause className="w-4 h-4" />
                      </Button>
                    ) : null}
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(wf.id)} title="Delete">
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination */}
      {totalWorkflows > pageSize && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalWorkflows)} of {totalWorkflows}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {Math.ceil(totalWorkflows / pageSize)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={currentPage >= Math.ceil(totalWorkflows / pageSize)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the workflow and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface MiniLineChartProps {
  title: string;
  data: any[];
  dataKey?: string;
  color?: string;
  lines?: { dataKey: string; color: string; name: string }[];
  computeTotal?: (d: any) => number;
  stat: number;
  statLabel?: string;
}

function MiniLineChart({ title, data, dataKey, color, lines, computeTotal, stat, statLabel }: MiniLineChartProps) {
  const chartData = React.useMemo(() => {
    if (computeTotal) {
      return data.map(d => ({ ...d, total: computeTotal(d) }));
    }
    return data;
  }, [data, computeTotal]);

  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</span>
          <div className="text-right">
            <span className="text-xl font-bold">{stat}</span>
            {statLabel && <span className="text-xs text-muted-foreground ml-1">{statLabel}</span>}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={80}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
            <Tooltip
              content={<MiniChartTooltip />}
              wrapperStyle={{ zIndex: 50 }}
            />
            {lines ? (
              lines.map(l => (
                <Line
                  key={l.dataKey}
                  type="monotone"
                  dataKey={l.dataKey}
                  stroke={l.color}
                  strokeWidth={1.5}
                  dot={false}
                  name={l.name}
                />
              ))
            ) : (
              <Line
                type="monotone"
                dataKey={dataKey || 'total'}
                stroke={color || '#3b82f6'}
                strokeWidth={1.5}
                dot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function MiniChartTooltip({ active, payload }: any) {
  if (!active || !payload || payload.length === 0) return null;
  const dateStr = (() => {
    try {
      const raw = payload[0]?.payload?.date;
      if (!raw) return '';
      return new Date(raw + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch { return ''; }
  })();
  return (
    <div className="bg-popover border rounded-md shadow-lg px-2.5 py-1.5 text-xs">
      {dateStr && <p className="text-muted-foreground mb-0.5">{dateStr}</p>}
      {payload.map((entry: any, i: number) => (
        <div key={i} className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}</span>
          <span className="font-medium ml-auto pl-2">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

const SOURCE_DISPLAY: Record<string, { label: string; icon: string }> = {
  kubernetes: { label: 'Kubernetes', icon: 'kubernetes' },
  aws: { label: 'AWS', icon: 'aws' },
  request: { label: 'Slack Requests', icon: 'slack' },
  pagerduty: { label: 'PagerDuty', icon: 'pagerduty' },
  posthog: { label: 'PostHog', icon: 'posthog' },
  vercel: { label: 'Vercel', icon: 'vercel' },
};

function SuggestedWorkflowsSection({
  suggestions,
  onUseSuggestion,
  onDismiss,
  forceOpen,
  onForceOpenConsumed,
  isGenerating,
}: {
  suggestions: SuggestedWorkflow[];
  onUseSuggestion: (s: SuggestedWorkflow) => void;
  onDismiss: () => void;
  forceOpen?: boolean;
  onForceOpenConsumed?: () => void;
  isGenerating?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  useEffect(() => {
    if (forceOpen) {
      setCollapsed(false);
      onForceOpenConsumed?.();
    }
  }, [forceOpen, onForceOpenConsumed]);

  const grouped = suggestions.reduce<Record<string, SuggestedWorkflow[]>>((acc, s) => {
    const src = s.source || 'kubernetes';
    if (!acc[src]) acc[src] = [];
    acc[src].push(s);
    return acc;
  }, {});

  const sourceOrder = ['kubernetes', 'aws', 'request', 'pagerduty', 'posthog', 'vercel'];
  const sortedSources = Object.keys(grouped).sort((a, b) => {
    const ai = sourceOrder.indexOf(a);
    const bi = sourceOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 text-sm font-semibold hover:text-foreground transition-colors"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          <Sparkles className="w-4 h-4 text-primary" />
          Suggested Workflows ({suggestions.length})
          {isGenerating && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </button>
        <Button variant="ghost" size="sm" onClick={onDismiss} className="text-xs text-muted-foreground">
          Dismiss
        </Button>
      </div>
      {!collapsed && (
        <div className="space-y-4">
          {sortedSources.map(source => {
            const display = SOURCE_DISPLAY[source] || { label: source, icon: source };
            const items = grouped[source];
            return (
              <div key={source}>
                <div className="flex items-center gap-2 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <IntegrationIconImg integration={display.icon} isDark={isDark} size={14} />
                  {display.label}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((s, i) => (
                    <Card key={i} className="border-dashed border-primary/30 hover:border-primary/60 transition-colors cursor-pointer group" onClick={() => onUseSuggestion(s)}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-sm font-semibold leading-tight">{s.name}</h4>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {s.definition?.nodes?.length || 0} steps
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{s.description}</p>
                        <Button variant="outline" size="sm" className="w-full text-xs gap-1 opacity-80 group-hover:opacity-100">
                          <Plus className="w-3 h-3" /> Use This Workflow
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default WorkflowList;
